import { useState, useEffect } from "react";

// Types for Ad Configuration
export type AdType = 'google_ads' | 'image_banner' | 'custom_html';

export interface AdPlacement {
    id: string;
    label: string;
    description: string;
    type: AdType;
    width: string; // CSS width (e.g. "100%", "300px")
    height: string; // CSS height (e.g. "90px", "250px")
    enabled: boolean;
    // Google Ads Props
    googleAdClient?: string; // ca-pub-XXXXXXXXXXXXXXXX
    googleAdSlot?: string;
    googleAdFormat?: 'auto' | 'fluid' | 'rectangle' | 'vertical';
    googleFullWidthResponsive?: boolean;
    // Image Banner Props
    imageUrl?: string;
    destinationUrl?: string;
    // House Ad Fallback
    showFallback: boolean;
}

// Default Configuration
const DEFAULT_CONFIGS: AdPlacement[] = [
    {
        id: "home_leaderboard_1",
        label: "Homepage - Top Banner",
        description: "Appears below the hero section on the homepage.",
        type: "google_ads",
        width: "100%",
        height: "auto",
        enabled: true,
        googleAdClient: "ca-pub-7709482445735151",
        googleAdSlot: "1234567890",
        googleAdFormat: "auto",
        googleFullWidthResponsive: true,
        showFallback: true
    },
    {
        id: "home_banner_2",
        label: "Homepage - Middle Banner",
        description: "Appears in the middle of the homepage content.",
        type: "google_ads",
        width: "100%",
        height: "auto",
        enabled: true,
        googleAdClient: "ca-pub-7709482445735151",
        googleAdSlot: "2345678901",
        googleAdFormat: "auto",
        googleFullWidthResponsive: true,
        showFallback: true
    },
    {
        id: "sidebar_rectangle_1",
        label: "Listing Sidebar - Top",
        description: "Square ad in the sidebar of listing pages (Vets, Trainers, etc).",
        type: "google_ads",
        width: "100%",
        height: "250px",
        enabled: true,
        googleAdClient: "ca-pub-7709482445735151",
        googleAdSlot: "0987654321",
        googleAdFormat: "rectangle",
        showFallback: true
    },
    {
        id: "listing_infeed_1",
        label: "Listing Feed - Middle",
        description: "Appears between cards in the listing grid.",
        type: "image_banner", // Default to image to look better if no AdSense
        width: "100%",
        height: "auto",
        enabled: true,
        imageUrl: "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        destinationUrl: "/premium-membership",
        showFallback: true
    },
    {
        id: "profile_bottom_banner",
        label: "Profile Page - Bottom",
        description: "Banner at the bottom of profile pages.",
        type: "google_ads",
        width: "100%",
        height: "auto",
        enabled: true,
        googleAdClient: "ca-pub-7709482445735151",
        googleAdSlot: "1122334455",
        googleAdFormat: "auto",
        googleFullWidthResponsive: true,
        showFallback: true
    },
    {
        id: "pet_detail_sidebar",
        label: "Pet Detail Sidebar",
        description: "Vertical ad in the sidebar of the pet detail page.",
        type: "google_ads",
        width: "100%",
        height: "600px",
        enabled: true,
        googleAdClient: "ca-pub-7709482445735151",
        googleAdSlot: "1231231234",
        googleAdFormat: "vertical",
        googleFullWidthResponsive: true,
        showFallback: true
    }
];

const STORAGE_KEY = "pawsindia_ad_config";

export const getAdConfigs = (): AdPlacement[] => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error("Error loading ad config", e);
    }
    return DEFAULT_CONFIGS;
};

export const saveAdConfigs = (configs: AdPlacement[]) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
        // Dispatch event to update components (basic reactivity)
        window.dispatchEvent(new Event("adConfigUpdated"));
    } catch (e) {
        console.error("Error saving ad config", e);
    }
};

export const getAdConfig = (placementId: string): AdPlacement | undefined => {
    const configs = getAdConfigs();
    return configs.find(c => c.id === placementId);
};

export const useAdConfig = (placementId: string) => {
    const [config, setConfig] = useState<AdPlacement | undefined>(getAdConfig(placementId));

    useEffect(() => {
        const handleUpdate = () => {
            setConfig(getAdConfig(placementId));
        };
        window.addEventListener("adConfigUpdated", handleUpdate);
        return () => window.removeEventListener("adConfigUpdated", handleUpdate);
    }, [placementId]);

    return config;
};
